import dayjs from 'dayjs';
import type {
	ManualEdit,
	ManualEditForm,
	ManualEditIssue,
} from '../../redux/api/traveler-audit';

export const MAX_INITIALS = 5;
/** Date the reviewer sees and types. */
export const DISPLAY_DATE = 'MM/DD/YYYY';
/** Date sent to the API. */
export const API_DATE = 'YYYY-MM-DD';

/** Typed values per field; blank means "leave as is". */
export type StampDraft = { initials: string; date: string };
export type ManualDraft = {
	steps: Record<string, StampDraft>;
	/** Keyed by ``faiKey(balloon, index)``. */
	fai: Record<string, StampDraft>;
	revision: string;
	/** Undefined until the reviewer touches the checkbox. */
	certNa?: boolean;
};

export const emptyDraft = (): ManualDraft => ({
	steps: {},
	fai: {},
	revision: '',
});

export const faiKey = (balloon: string, index: number) => `${balloon}.${index}`;

export const isMissing = (value?: string | null) =>
	value == null || value.trim() === '' || value.trim().toUpperCase() === 'N/A';

/** Uppercase letters and digits only, at most ``MAX_INITIALS`` (the server rule). */
export const normalizeInitials = (value: string) =>
	value
		.toUpperCase()
		.replace(/[^A-Z0-9]/g, '')
		.slice(0, MAX_INITIALS);

const SHORT_DATE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/;
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * FAI sign-off date -> ``YYYY-MM-DD``, or '' when absent or unreadable.
 * Accepts ``MM/DD/YY``, ``MM/DD/YYYY``, ``MM-DD-YY`` and ``YYYY-MM-DD``, the
 * same formats the validator reads.
 */
export const faiDateToDay = (value?: string | null) => {
	if (isMissing(value)) return '';
	const text = (value as string).trim();
	if (ISO_DAY.test(text)) return text;
	const match = SHORT_DATE.exec(text);
	if (!match) return '';
	const [, mm, dd, yy] = match;
	const year = yy.length === 2 ? `20${yy}` : yy;
	return `${year}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
};

export const dayToDisplay = (day?: string | null) =>
	day ? dayjs(day).format(DISPLAY_DATE) : '';

export const hasIssues = (row: { issues: ManualEditIssue[] }) =>
	row.issues.length > 0;

/** Flagged rows first; original order kept inside each group. */
export const flaggedFirst = <T extends { issues: ManualEditIssue[] }>(
	rows: T[]
) => [...rows.filter(hasIssues), ...rows.filter((r) => !hasIssues(r))];

export const worstSeverity = (issues: ManualEditIssue[]) =>
	issues.some((i) => i.severity === 'fail')
		? 'fail'
		: issues.length
		? 'review'
		: null;

const stampEdit = (
	draft: StampDraft | undefined,
	currentInitials: string | null,
	currentDay: string,
	keys: { initials: string; date: string }
) => {
	if (!draft) return null;
	const initials = normalizeInitials(draft.initials || '');
	const date = (draft.date || '').trim();
	const initialsChanged =
		initials !== '' && initials !== (currentInitials || '').toUpperCase();
	const dateChanged = date !== '' && date !== currentDay;
	if (!initialsChanged && !dateChanged) return null;
	return {
		[keys.initials]: initialsChanged ? initials : '',
		[keys.date]: dateChanged ? date : '',
	};
};

/**
 * Only the fields whose typed value differs from what is on the packet. Each
 * edit carries ``expected`` (what the screen showed) so a save against a
 * packet that changed underneath is refused instead of applied blind.
 */
export function buildEdits(
	form: ManualEditForm,
	draft: ManualDraft
): ManualEdit[] {
	const edits: ManualEdit[] = [];
	for (const step of form.steps) {
		const value = stampEdit(draft.steps[step.key], step.user, step.day ?? '', {
			initials: 'user',
			date: 'at',
		});
		if (value) {
			edits.push({
				field_key: `step.${step.key}`,
				value,
				expected: { user: step.user, day: step.day },
			});
		}
	}
	const revision = draft.revision.trim();
	if (revision && revision !== (form.revision.value || '').trim()) {
		edits.push({
			field_key: 'revision',
			value: revision,
			expected: form.revision.value,
		});
	}
	if (
		draft.certNa !== undefined &&
		draft.certNa !== form.material_cert.not_applicable
	) {
		edits.push({
			field_key: 'material_cert_na',
			value: draft.certNa,
			expected: form.material_cert.not_applicable,
		});
	}
	for (const balloon of form.fai) {
		if (!balloon.editable) continue;
		for (const insp of balloon.inspectors) {
			const value = stampEdit(
				draft.fai[faiKey(balloon.balloon, insp.index)],
				insp.initials,
				faiDateToDay(insp.date),
				{ initials: 'initials', date: 'date' }
			);
			if (value) {
				edits.push({
					field_key: `fai.inspector.${balloon.balloon}.${insp.index}`,
					value,
					expected: { initials: insp.initials, date: insp.date },
				});
			}
		}
	}
	return edits;
}

/** Balloons shown by default: flagged ones, or every balloon when toggled. */
export const visibleBalloons = (form: ManualEditForm, showAll: boolean) =>
	showAll ? flaggedFirst(form.fai) : form.fai.filter(hasIssues);
